import { useMutation } from '@tanstack/react-query';
import { arrayComparison } from '@/src/utils/utils';
import { AxiosError } from 'axios';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import React, { useEffect, useRef, useState } from 'react';
import RejectionField from '../../../../components/dialog/RejectionField';
import LoadingButton from '../../../../components/inputs/LoadingButton';
import ConfirmationDialog from '../../../../components/messages/ConfirmationDialog';
import ErrorToast, {
  showToast,
} from '../../../../components/messages/ErrorAlert';
import { useCompanyContext } from '../../../../context/CompanyContext';
import { useProcessContext } from '../../../../context/ProcessContext';
import {
  EmployeeTimesheet,
  TimeSheetArgs,
} from '../../../../Interfaces/Accounting/ACForms.interfaces';
import {
  EmployeePCReimbursement,
  PCReimbursementArgs,
} from '../../../../Interfaces/Accounting/Payroll.interface';
import { Access } from '../../../../Interfaces/admin/Role.interfaces';
import {
  postPCReimbursementDraft,
  postTimesheetDraft,
  putPCReimbursementDraft,
  putTimesheetApproveDraft,
  putTimesheetDraft,
  putTimesheetRejectDraft,
  putTimesheetSubmitDraft,
  putTimesheetUploadDraft,
} from '../../../../services/accounting/PayrollService';
import {
  checkReimbursementContent,
  prepareReimbursementPayload,
} from '../PettyCashReimbursement/utils';
import {
  checkTimesheetContent,
  generateConfirmationMessage,
  prepareTimesheetPayload,
} from './util';

type TimesheetProcessProps = {
  approvalMode: boolean;
  timesheet: EmployeeTimesheet[];
  reimbursement: EmployeePCReimbursement[];
  periodStart: Date;
  periodEnd: Date;
  timesheetID: string;
  updateTimesheetID: (value: string) => void;
  defaultReimbursementID?: string;
  blockAll?: boolean;
  access: Access;
  afterCheck: (showErrors: boolean) => void;
  afterUpdate: (
    data: EmployeePCReimbursement[],
    newSizeLimit: number,
    id: string
  ) => void;
  step: number;
  changeStep: (step: number) => void;
  resetDraft: () => void;
  filesToDeleteDefault: Record<number, string[]>;
  afterApproval?: (draftID: string) => void;
  fromLastTimesheet?: boolean;
};

const TimesheetBundleProcess = ({
  timesheetID,
  updateTimesheetID,
  defaultReimbursementID,
  approvalMode,
  timesheet,
  reimbursement,
  periodStart,
  periodEnd,
  blockAll,
  access,
  afterCheck,
  afterUpdate,
  step,
  changeStep,
  resetDraft,
  filesToDeleteDefault,
  afterApproval,
  fromLastTimesheet,
}: TimesheetProcessProps) => {
  const { selectedCompany } = useCompanyContext();
  const processStatus = useProcessContext();
  const toast = useRef<Toast>(null);
  const [reimbursementID, setReimbursementID] = useState<string>(
    defaultReimbursementID ?? ''
  );
  const [visibility, setVisibility] = useState(false);
  const prevTimesheet = useRef(timesheet);
  const prevReimbursement = useRef(reimbursement);

  const saveReimbursement = useMutation({
    mutationFn: ({
      draftID,
      draft,
      timesheetID,
    }: {
      draftID: string;
      draft: PCReimbursementArgs;
      timesheetID?: string;
    }) => {
      if (draftID) {
        return putPCReimbursementDraft(draftID, draft, !!approvalMode);
      }

      return postPCReimbursementDraft(selectedCompany!.id, draft, timesheetID);
    },
    onSuccess: (data) => {
      let message = 'The data was saved successfully!';
      if (data.error.length) {
        message = `The data was saved with the exception of the following files: ${data.error.join(
          ', '
        )}.`;
      }

      showToast('success', toast, 'Save Payroll Timesheet', message, 3000);

      afterUpdate(
        data.draft.employees,
        data.draft.attachmentsSize,
        data.draft.id
      );

      processStatus.setSave(false);
      processStatus.setSubmit(true);
      processStatus.setApprove(true);

      if (!reimbursementID) {
        setReimbursementID(data.draft.id);
      }

      afterCheck(false);
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as {
        code: string;
        message: string;
      };
      showToast(
        'error',
        toast,
        'Save Payroll Timesheet',
        errorData?.message || `The data couldn't be saved`,
        3000
      );
    },
  });

  const saveReimbursementDraft = (
    draftID: string,
    draft: EmployeePCReimbursement[],
    timesheetID: string
  ) => {
    const processedDraft = prepareReimbursementPayload(draft);

    processedDraft.forEach((emp) => {
      const prevAttachments = filesToDeleteDefault[emp.empID];

      if (prevAttachments) {
        emp.deleteAttachments = prevAttachments;
      }
    });

    saveReimbursement.mutate({ draftID, timesheetID, draft: processedDraft });
  };

  const saveTimesheet = useMutation({
    mutationFn: ({
      draftID,
      timesheet,
    }: {
      draftID: string;
      timesheet: TimeSheetArgs;
    }) => {
      if (draftID) {
        return putTimesheetDraft(
          draftID,
          timesheet,
          periodStart,
          periodEnd,
          !!approvalMode,
          undefined,
          fromLastTimesheet
        );
      }

      return postTimesheetDraft(
        selectedCompany!.id,
        timesheet,
        periodStart,
        periodEnd,
        fromLastTimesheet
      );
    },
    onSuccess: (data) => {
      if (!timesheetID) {
        updateTimesheetID(data);
        localStorage.setItem('temp-timesheet-stored-id', data ?? '');
      }

      saveReimbursementDraft(reimbursementID, reimbursement, data);
    },
    onError: () => {
      showToast(
        'error',
        toast,
        'Save Payroll Timesheet',
        "The data couldn't be saved",
        3000
      );
    },
  });

  const saveDraft = (draftID: string, timesheet: EmployeeTimesheet[]) => {
    const processedDraft = prepareTimesheetPayload(timesheet);
    saveTimesheet.mutate({ draftID, timesheet: processedDraft });
  };

  const submitTimesheet = useMutation({
    mutationFn: (draftID: string) => {
      return putTimesheetSubmitDraft(draftID);
    },
    onSuccess: () => {
      processStatus.setSubmit(false);

      showToast(
        'success',
        toast,
        'Submit Payroll Timesheet',
        'The data was submitted successfully!',
        3000
      );
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as {
        code: string;
        message: string;
      };

      showToast(
        'error',
        toast,
        'Submit Payroll Timesheet',
        errorData?.message || "The data couldn't be submitted",
        3000
      );
    },
  });

  const approveTimesheet = useMutation({
    mutationFn: (draftID: string) => {
      return putTimesheetApproveDraft(draftID);
    },
    onSuccess: () => {
      showToast(
        'success',
        toast,
        `Approve Payroll Timesheet`,
        `The draft was approved successfully!`,
        3000
      );

      processStatus.setApprove(false);
      processStatus.setAlreadyApproved(true);
      if (afterApproval) afterApproval(timesheetID);
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as {
        code: string;
        message: string;
      };
      showToast(
        'error',
        toast,
        `Approve Payroll Timesheet`,
        errorData?.message || `The draft couldn't be approved`,
        3000
      );
    },
  });

  const uploadRequest = useMutation({
    mutationFn: (draftID: string) => {
      return putTimesheetUploadDraft(draftID);
    },
    onSuccess: () => {
      showToast(
        'success',
        toast,
        `Upload Payroll Timesheet`,
        `The draft was uploaded successfully!`,
        3000
      );

      processStatus.setApprove(false);
      processStatus.setAlreadyApproved(true);
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as {
        code: string;
        message: string;
      };
      showToast(
        'error',
        toast,
        `Upload Payroll Timesheet`,
        errorData?.message || `The draft couldn't be uploaded`,
        3000
      );
    },
  });

  const rejectRequest = useMutation({
    mutationFn: (props: { draftID: string; reason: string }) => {
      return putTimesheetRejectDraft(props.draftID, props.reason);
    },
    onSuccess: () => {
      showToast(
        'success',
        toast,
        `Reject Payroll Timesheet`,
        `The draft was rejected successfully!`,
        3000
      );

      processStatus.setApprove(false);
      processStatus.setAlreadyApproved(true);
      if (afterApproval) afterApproval(timesheetID);
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as {
        code: string;
        message: string;
      };

      showToast(
        'error',
        toast,
        `Reject Payroll Timesheet`,
        errorData?.message || `The draft couldn't be rejected`,
        3000
      );
    },
  });

  const formErrorMessage = (text: string) => {
    showToast('error', toast, `Payroll Timesheet`, text, 3000);
  };

  useEffect(() => {
    if (prevTimesheet.current !== timesheet && !processStatus.alreadyApproved) {
      processStatus.setSave(true);
      processStatus.setSubmit(false);
      processStatus.setApprove(false);
      prevTimesheet.current = timesheet;
    }
  }, [timesheet, processStatus.alreadyApproved]);

  useEffect(() => {
    if (
      prevReimbursement.current !== reimbursement &&
      !processStatus.alreadyApproved
    ) {
      const areEquals = arrayComparison(
        prevReimbursement.current,
        reimbursement
      );

      if (!areEquals) {
        processStatus.setSave(true);
        processStatus.setSubmit(false);
        processStatus.setApprove(false);
        prevReimbursement.current = reimbursement;
      }
    }
  }, [reimbursement, processStatus.alreadyApproved]);

  return (
    <React.Fragment>
      <ErrorToast toastRef={toast} />
      <div className="sm:mr-auto">
        <ConfirmationDialog
          tagKey="draft-clear"
          Button={
            <LoadingButton
              label="Clear Form"
              fontSize="text-xl"
              bgColor="bg-orange-500"
              isLoading={false}
              type="button"
              disabled={processStatus.alreadyApproved}
            />
          }
          onConfirm={() => {
            resetDraft();
          }}
          message={'Are you sure you want to clear the form data?'}
        />
      </div>
      <div className="flex flex-wrap gap-4">
        <Button
          type="button"
          className={`w-10rem bluwaiBlue border-0 flex m-auto py-3 relative`}
          onClick={() => {
            if (step === 0) {
              changeStep(1);
            } else {
              changeStep(0);
            }
          }}
        >
          <span className={`mx-auto text-xl text-white`}>
            {step === 0 ? 'Next' : 'Previous'}
          </span>
        </Button>
        <LoadingButton
          label="Save"
          fontSize="text-xl"
          bgColor="buttonSecondary"
          isLoading={saveTimesheet.isLoading || saveReimbursement.isLoading}
          onClick={() => saveDraft(timesheetID, timesheet)}
          disabled={
            blockAll ||
            !processStatus.save ||
            saveTimesheet.isLoading ||
            saveReimbursement.isLoading
          }
        />
        {!approvalMode && access.editable === 'approval' && (
          <ConfirmationDialog
            Button={
              <LoadingButton
                label="Submit"
                fontSize="text-xl"
                isLoading={submitTimesheet.isLoading}
                disabled={
                  blockAll || !processStatus.submit || submitTimesheet.isLoading
                }
                type="button"
              />
            }
            onConfirm={() => {
              const { missingPhases, missingCertifiedHours } =
                checkTimesheetContent(timesheet);
              const isReimbursementValid =
                checkReimbursementContent(reimbursement);

              if (missingPhases) {
                afterCheck(true);
                formErrorMessage(
                  'The Timesheet form is missing some phase codes!'
                );
              } else if (missingCertifiedHours) {
                afterCheck(true);
                formErrorMessage(
                  'An employee with a certified job is missing some hours or dates!'
                );
              } else if (!isReimbursementValid) {
                afterCheck(true);
                formErrorMessage(
                  `The Reimbursement form is missing some fields!`
                );
              } else {
                submitTimesheet.mutate(timesheetID);
                afterCheck(false);
              }
            }}
            message={generateConfirmationMessage(
              `Are you sure you want to submit this draft?`,
              timesheet
            )}
          />
        )}
        {approvalMode && access.shouldApprove && (
          <ConfirmationDialog
            tagKey="draft-rejection"
            visibility={visibility}
            Button={
              <LoadingButton
                label={'Reject'}
                fontSize="text-xl"
                bgColor="bluwaiRed"
                isLoading={rejectRequest.isLoading}
                disabled={
                  blockAll || !processStatus.approve || rejectRequest.isLoading
                }
                type="button"
                onClick={() => setVisibility(true)}
              />
            }
            contentClassName="pb-0"
            acceptClassName="hidden"
            rejectClassName="hidden"
            onHide={() => setVisibility(false)}
            message={
              <RejectionField
                onReject={() => {
                  setVisibility(false);
                }}
                onConfirm={(reason) => {
                  rejectRequest.mutate({ draftID: timesheetID, reason });
                  setVisibility(false);
                }}
              />
            }
          />
        )}
        {approvalMode && access.shouldApprove && (
          <ConfirmationDialog
            tagKey="draft-approval"
            Button={
              <LoadingButton
                label={'Approve'}
                fontSize="text-xl"
                bgColor="bg-green-500"
                isLoading={approveTimesheet.isLoading}
                disabled={
                  blockAll ||
                  !processStatus.approve ||
                  approveTimesheet.isLoading
                }
                type="button"
              />
            }
            onConfirm={() => {
              const { missingPhases, missingCertifiedHours } =
                checkTimesheetContent(timesheet);
              const isReimbursementValid =
                checkReimbursementContent(reimbursement);

              if (missingPhases) {
                afterCheck(true);
                formErrorMessage(
                  'The Timesheet form is missing some phase codes!'
                );
              } else if (missingCertifiedHours) {
                afterCheck(true);
                formErrorMessage(
                  'An employee with a certified job is missing some hours or dates!'
                );
              } else if (!isReimbursementValid) {
                afterCheck(true);
                formErrorMessage(
                  `The Reimbursement form is missing some fields!`
                );
              } else {
                approveTimesheet.mutate(timesheetID);
                afterCheck(false);
              }
            }}
            message={generateConfirmationMessage(
              `Are you sure you want to approve this draft?`,
              timesheet
            )}
          />
        )}
        {!approvalMode && access.editable === 'no-approval' && (
          <ConfirmationDialog
            tagKey="draft-upload"
            Button={
              <LoadingButton
                label={'Upload'}
                fontSize="text-xl"
                bgColor="bg-green-500"
                isLoading={uploadRequest.isLoading}
                disabled={
                  blockAll || !processStatus.approve || uploadRequest.isLoading
                }
                type="button"
              />
            }
            onConfirm={() => {
              const { missingPhases, missingCertifiedHours } =
                checkTimesheetContent(timesheet);
              const isReimbursementValid =
                checkReimbursementContent(reimbursement);

              if (missingPhases) {
                afterCheck(true);
                formErrorMessage(
                  'The Timesheet form is missing some phase codes!'
                );
              } else if (missingCertifiedHours) {
                afterCheck(true);
                formErrorMessage(
                  'An employee with a certified job is missing some hours or dates!'
                );
              } else if (!isReimbursementValid) {
                afterCheck(true);
                formErrorMessage(
                  `The Reimbursement form is missing some fields!`
                );
              } else {
                uploadRequest.mutate(timesheetID);
                afterCheck(false);
              }
            }}
            message={generateConfirmationMessage(
              `Are you sure you want to upload this draft?`,
              timesheet
            )}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default TimesheetBundleProcess;
